import { Fragment, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../../bridges/session";
import { Notice } from "../../ui";
import { isApiError } from "../../data/errors";
import { useNextT } from "../../i18n";
import { errorText } from "../errors";
import { refreshAfterListWrite } from "../api";
import type { T } from "../format";

/** Shared by the contacts dialogs (bulk, row pickers, delete, download). */

const MARK = "\u0001";

/** A translated sentence with React nodes in some placeholders (bold numbers, user text in <bdi>), in the language's own word order. */
export const rich = (t: T, key: string, opts: Record<string, unknown>, nodes: Record<string, ReactNode>): ReactNode => {
  const marks = Object.fromEntries(Object.keys(nodes).map((k) => [k, `${MARK}${k}${MARK}`]));
  const parts = t(key, { ...opts, ...marks }).split(MARK);
  return <>{parts.map((p, i) => <Fragment key={i}>{i % 2 ? nodes[p] : p}</Fragment>)}</>;
};

/**
 * Error handler for the actions. SELECTION_EMPTY means none of these contacts exist any more: refresh the list,
 * then finish like a success (selection cleared, dialog closed) with the message as the toast. Others stay in the dialog.
 */
export const useSelectionGone = (onDone: (message: string) => void) => {
  const { t } = useNextT();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return (e: unknown): boolean => {
    if (!isApiError(e) || e.code !== "SELECTION_EMPTY") return false;
    void refreshAfterListWrite(client, ws);
    onDone(errorText(e, t));
    return true;
  };
};

/** The error a dialog shows inline: SELECTION_EMPTY is handled by `useSelectionGone` instead. */
export const shownError = (e: unknown) => (isApiError(e) && e.code === "SELECTION_EMPTY" ? null : e);

/** Inline refusal in a dialog. A 5xx shows nothing here: the app's interceptor already announces it. */
export const ErrorNotice = ({ error }: { error: unknown }) => {
  const { t } = useNextT();
  if (!error || (isApiError(error) && error.kind === "server")) return null;
  return <Notice tone="danger" title={errorText(error, t)} />;
};
